import { createTheme, ThemeOptions, Shadows } from '@mui/material/styles';
import { muiTypographyConfig } from './typography';
import { drisSemanticColors, drisAnalysisColors, drisCorporateColors } from './colors';
import { drisDarkPalette } from './darkPalette';

// DRIS Brand Colors - Now using corporate identity colors
const DRIS_COLORS = {
  ...drisSemanticColors,
  grey: {
    50: drisCorporateColors.veryLightGray,
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: drisCorporateColors.darkPurple,
    900: drisCorporateColors.veryDarkPurple,
  },
  // DRIS Analysis Type Colors
  analysis: drisAnalysisColors,
  // Additional corporate colors
  lightOrange: drisCorporateColors.lightOrange,
} as const;

// DRIS Spacing Scale (8px base unit)
const DRIS_SPACING = {
  xs: 4,    // 0.25rem
  sm: 8,    // 0.5rem
  md: 16,   // 1rem
  lg: 24,   // 1.5rem
  xl: 32,   // 2rem
  xxl: 48,  // 3rem
  xxxl: 64, // 4rem
} as const;

// DRIS Border Radius Scale
const DRIS_RADIUS = {
  sm: 4,    // 0.25rem
  md: 8,    // 0.5rem
  lg: 12,   // 0.75rem
  xl: 16,   // 1rem
} as const;

// DRIS Shadows - Enhanced with more depth
const DRIS_SHADOWS = {
  light: '0 2px 4px rgba(0, 0, 0, 0.06)',
  medium: '0 4px 6px rgba(0, 0, 0, 0.1)',
  dark: '0 10px 25px rgba(0, 0, 0, 0.15)',
  xlarge: '0 25px 50px rgba(0, 0, 0, 0.25)',
  focus: '0 0 0 3px rgba(46, 125, 50, 0.2)',
  card: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  hover: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
} as const;

// DRIS Breakpoints
const DRIS_BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
} as const;

// DRIS Transitions - Smoother animations
const DRIS_TRANSITIONS = {
  fast: '0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  smooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

// DRIS Z-Index Scale
const DRIS_Z_INDEX = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
} as const;

// Create the DRIS Material-UI Theme with dark mode support
export const createDRISTheme = (): ThemeOptions => ({
  cssVariables: {
    colorSchemeSelector: 'class',
  },
  colorSchemes: {
    light: {
      palette: {
        primary: DRIS_COLORS.primary,
        secondary: DRIS_COLORS.secondary,
        success: DRIS_COLORS.success,
        warning: DRIS_COLORS.warning,
        error: DRIS_COLORS.error,
        info: DRIS_COLORS.info,
        grey: DRIS_COLORS.grey,
        text: DRIS_COLORS.text,
        background: DRIS_COLORS.background,
        divider: DRIS_COLORS.divider,
        analysis: DRIS_COLORS.analysis,
      },
    },
    dark: {
      palette: drisDarkPalette,
    },
  },
  typography: muiTypographyConfig,
  spacing: (factor: number) => `${factor * 8}px`,
  shape: {
    borderRadius: DRIS_RADIUS.md,
  },
  shadows: [
    'none',
    DRIS_SHADOWS.light,
    DRIS_SHADOWS.medium,
    DRIS_SHADOWS.dark,
    DRIS_SHADOWS.hover,
    DRIS_SHADOWS.xlarge,
    // Material-UI requires exactly 25 shadows
    ...Array(19).fill('none'),
  ] as Shadows,
  breakpoints: {
    values: DRIS_BREAKPOINTS,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: DRIS_RADIUS.lg,
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 48,
          padding: '14px 28px',
          transition: DRIS_TRANSITIONS.smooth,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'var(--dris-shadow-hover)',
          },
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.vars.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.vars.palette.primary.dark,
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          borderColor: theme.vars.palette.primary.main,
          color: theme.vars.palette.primary.main,
          borderWidth: '2px',
          '&:hover': {
            backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.06)`,
            borderWidth: '2px',
            borderColor: theme.vars.palette.primary.dark,
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: DRIS_RADIUS.lg,
          boxShadow: 'var(--dris-shadow-card)',
          transition: DRIS_TRANSITIONS.smooth,
          border: `1px solid ${theme.vars.palette.divider}`,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: DRIS_RADIUS.md,
            minHeight: 48,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.vars.palette.primary.main,
              boxShadow: 'var(--dris-shadow-focus)',
            },
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: DRIS_SHADOWS.light,
        },
        elevation2: {
          boxShadow: DRIS_SHADOWS.medium,
        },
        elevation3: {
          boxShadow: DRIS_SHADOWS.dark,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
          color: theme.vars.palette.text.primary,
          boxShadow: DRIS_SHADOWS.light,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
          borderRight: `1px solid ${theme.vars.palette.divider}`,
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: DRIS_RADIUS.md,
          margin: '4px 8px',
          '&.Mui-selected': {
            backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.12)`,
            color: theme.vars.palette.primary.main,
            '&:hover': {
              backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.18)`,
            },
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: DRIS_RADIUS.md,
          fontWeight: 500,
        },
        colorPrimary: ({ theme }) => ({
          backgroundColor: theme.vars.palette.primary.main,
          color: theme.vars.palette.primary.contrastText,
        }),
        colorSecondary: ({ theme }) => ({
          backgroundColor: theme.vars.palette.secondary.main,
          color: theme.vars.palette.secondary.contrastText,
        }),
      },
    },
  },
});

// Export constants for use in components
export {
  DRIS_COLORS,
  DRIS_SPACING,
  DRIS_RADIUS,
  DRIS_SHADOWS,
  DRIS_BREAKPOINTS,
  DRIS_TRANSITIONS,
  DRIS_Z_INDEX,
};

// Re-export typography from centralized configuration
export { muiTypographyConfig as DRIS_TYPOGRAPHY } from './typography';

// Default theme instance
export const drisTheme = createTheme(createDRISTheme());

export default drisTheme;
